import { useCallback, useMemo, useState } from 'react';
import { ButtonVariants, Modal, ModalSizes, Table } from '@carboledger/ui';
import { buildTableLabels } from '@/config/tableLabels';
import { useTranslations } from '@/hooks';
import { buildSiteAccessColumns } from '../constants/accessManagerColumns';
import { buildSiteAccessRows } from '../mappers/adminMappers';

const getAccessRowId = (row) => row.id;

const compare = (a, b) =>
  typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b), undefined, { numeric: true });

/**
 * Props:
 *   user     the list row whose access is shown; null closes the modal
 *   onClose  ✕ / Cancel
 */
export const SiteAccessModal = ({ user, onClose }) => {
  const t = useTranslations();
  const [sort, setSort] = useState(null);

  const rows = useMemo(() => {
    const base = buildSiteAccessRows(user);
    if (!sort) return base;
    const direction = sort.dir === 'desc' ? -1 : 1;
    return [...base].sort((a, b) => direction * compare(a[sort.id], b[sort.id]));
  }, [user, sort]);

  const columns = useMemo(() => buildSiteAccessColumns(t), [t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const handleClose = useCallback(() => {
    setSort(null);
    onClose();
  }, [onClose]);

  const buttons = useMemo(
    () => [{ label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose }],
    [t, handleClose],
  );

  return (
    <Modal
      open={user != null}
      size={ModalSizes.medium}
      title={t('ADMIN_SITE_ACCESS_TITLE')}
      onClose={handleClose}
      buttons={buttons}
    >
      <Table
        columns={columns}
        data={rows}
        getRowId={getAccessRowId}
        onSortChange={setSort}
        showPagination={false}
        labels={tableLabels}
      />
    </Modal>
  );
};
